import Typography from "@mui/joy/Typography";
import { Box, Button, CircularProgress, Stack } from "@mui/joy";
import { MeditationItemType, ThoughtRecordItemType } from "api/homeApi";
import NestedList, { menuKeyType } from "./NestedList";
import { TaskType } from "api";
import useOpenNewThoughtRecord from "hooks/useOpenNewThoughtRecord";
import useOpenNewMeditationRecord from "hooks/useOpenNewMeditationRecord";
import useOpenThoughtRecord from "hooks/useOpenThoughtRecord";
import useOpenMeditationRecord from "hooks/useOpenMeditationRecord";
import { useRecoilValue } from "recoil";
import { userAtom } from "recoil/userAtom";
import useIsInappWebview from "hooks/useIsInappWebview";
import {
  AddIcon,
  LearningActivityIcon,
  MeditationIcon,
  RightArrow,
  ThoughtRecordIcon,
} from "static/image/common/IconAssets";
import { theme } from "styles/theme";

export default function ActivityList({
  title,
  isInitialLoading,
  listData,
}: {
  title: string;
  isInitialLoading: boolean;
  listData?: {
    listTitle: string;
    thoughtRecordList?: ThoughtRecordItemType[];
    meditationList?: MeditationItemType[];
    taskList?: TaskType[];
    menuKey: menuKeyType;
    initiallyOpen?: boolean;
  }[];
}) {
  const user = useRecoilValue(userAtom);
  const isCoach = user?.role !== "patient";

  const isInappWebview = useIsInappWebview();

  const openThoughtRecord = useOpenThoughtRecord();
  const openMeditationRecord = useOpenMeditationRecord();
  const openNewThoughtRecord = useOpenNewThoughtRecord();
  const openNewMeditationRecord = useOpenNewMeditationRecord();

  const dataByTitle = [
    {
      title: "생각기록지",
      icon: <ThoughtRecordIcon />,
      onTitleClick: openThoughtRecord,
      buttonText: "기록하기",
      onButtonClick: openNewThoughtRecord,
      buttonDisabled: isCoach,
    },
    {
      title: "명상훈련지",
      icon: <MeditationIcon />,
      onTitleClick: openMeditationRecord,
      buttonText: "훈련하기",
      onButtonClick: openNewMeditationRecord,
      buttonDisabled: isCoach,
    },
    {
      title: "배움활동지",
      icon: <LearningActivityIcon />,
    },
  ];
  const currentData = dataByTitle.find((element) => element.title === title);

  return listData ? (
    <Stack
      sx={{
        width: "100%",
        mt: "8px",
        borderRadius: "16px",
        p: "20px",
        ...(isInappWebview && {
          backgroundColor: theme.vars.palette.Surface.Forward,
        }),
      }}
      spacing={"8px"}
    >
      <Stack
        height="36px"
        direction="row"
        justifyContent={"space-between"}
        alignItems={"center"}
      >
        <Typography
          variant="Body"
          sx={{
            fontWeight: 700,
            cursor: "pointer",
            color: theme.vars.palette.Content.Strong,
            "--Typography-gap": "8px",
          }}
          startDecorator={
            <Stack
              sx={{
                width: "20px",
                height: "20px",
                borderRadius: "3px",
              }}
              justifyContent="center"
              alignItems={"center"}
            >
              {currentData?.icon}
            </Stack>
          }
          {...(currentData?.onTitleClick && {
            endDecorator: (
              <Stack
                justifyContent="center"
                alignItems={"center"}
                sx={{
                  width: "20px",
                  height: "20px",
                  transform: "translateX(-6px)",
                }}
              >
                <RightArrow />
              </Stack>
            ),
            onClick: currentData.onTitleClick,
          })}
        >
          {title}
        </Typography>
        {!isCoach && currentData?.buttonText && (
          <Button
            size="sm"
            variant="Outlined"
            disabled={currentData.buttonDisabled}
            onClick={() => {
              if (title === "생각기록지") {
                openNewThoughtRecord();
              } else if (title === "명상훈련지") {
                openNewMeditationRecord();
              }
            }}
            sx={{
              "--Button-gap": "2px",
              pl: "10px",
              pr: "12px",
              fontWeight: 700,
            }}
            startDecorator={<AddIcon size={16} />}
          >
            {currentData.buttonText}
          </Button>
        )}
      </Stack>

      {!isInitialLoading ? (
        listData.map(
          (
            {
              listTitle,
              thoughtRecordList,
              meditationList,
              taskList,
              menuKey,
              initiallyOpen,
            },
            index
          ) => (
            <NestedList
              key={listTitle}
              menuKey={menuKey}
              title={listTitle}
              {...(thoughtRecordList && {
                thoughtRecordList: thoughtRecordList,
              })}
              {...(meditationList && { meditationList: meditationList })}
              {...(taskList && { taskList: taskList })}
              initiallyOpen={initiallyOpen}
            />
          )
        )
      ) : (
        <Stack
          justifyContent={"center"}
          alignItems="center"
          sx={{
            flexGrow: 1,
          }}
        >
          <CircularProgress
            sx={{
              mt: "14px",
              mx: "auto",
            }}
            color="neutral"
          />
        </Stack>
      )}
    </Stack>
  ) : (
    <></>
  );
}
